/**
 * Mandatory cosmetic label pictograms.
 *
 * Real SVGs sourced from Wikimedia Commons (public-domain or CC-BY) and
 * stored in `public/icons/`. The same files render in two places:
 *   • As <img> elements inside the flat label preview.
 *   • Pre-loaded into HTMLImageElements and drawn onto the 3D bottle texture
 *     via canvas `drawImage`, so the bottle uses identical artwork.
 *
 * Sources:
 *   • PAO (12M)        — Wikimedia: 12M_PAO_Symbol.svg
 *   • Hourglass / BBE  — Wikimedia: Hourglass-cosmetics-directive.svg
 *   • Open book        — Wikimedia: ISO_7000_-_Ref-No_0790.svg
 *                        (ISO 7000 ref. 0790 "consult instructions for use",
 *                         used as the Annex VII §1 equivalent)
 *   • e-mark           — Wikimedia: Estimated_sign.svg (U+212E, ℮)
 *   • Möbius loop      — Wikimedia: Recycle001.svg
 *   • Triman           — Wikimedia: Pictogramme_TRIMAN_decret_2014-1577_NOR_DEVP1332154D.svg
 *
 * The Green Dot / Der Grüne Punkt is intentionally omitted because its use
 * requires a licensing agreement with Duales System Deutschland.
 */

import React from "react";

// All paths are relative to the public/ directory so they're served by both
// the dev server and the production build at the same URL.
const ICON_BASE = "/icons";

const ICON_FILE = {
  pao: `${ICON_BASE}/pao.svg`,
  hourglass: `${ICON_BASE}/hourglass.svg`,
  open_book: `${ICON_BASE}/open_book.svg`,
  e_mark: `${ICON_BASE}/e_mark.svg`,
  mobius: `${ICON_BASE}/mobius.svg`,
  triman: `${ICON_BASE}/triman.svg`,
  // CLP Reg. (EC) 1272/2008 / GHS hazard pictograms — required when a
  // cosmetic also qualifies as a hazardous mixture (e.g. ≥25% alcohol
  // makes a fragrance "Flam. Liq. 3" → GHS02 Flame; sensitisers /
  // category-1 health hazards trigger GHS07 / GHS08).
  ghs02_flame: `${ICON_BASE}/ghs02_flame.svg`,
  ghs07_exclamation: `${ICON_BASE}/ghs07_exclamation.svg`,
  ghs08_health: `${ICON_BASE}/ghs08_health.svg`,
};

// Each entry carries:
//   • label / citation / source — human-readable metadata
//   • minSizeMm — minimum print size from regulation or industry practice; the
//     UI surfaces this so designers don't undersize the icon at proof time
//   • wikimediaUrl — direct link to the source SVG file on Wikimedia Commons
//   • regulationUrl — link to the regulation text (or a citing reference)
//   • assetUrl — public URL we serve the SVG from (for downloads and the 3D
//     texture builder)
export const PICTOGRAM_INDEX = {
  pao: {
    label: "Period after opening",
    citation: "Reg. (EC) 1223/2009 Annex VII §2",
    source: "Wikimedia · 12M PAO Symbol.svg",
    minSizeMm: 5,
    wikimediaUrl: "https://commons.wikimedia.org/wiki/File:12M_PAO_Symbol.svg",
    regulationUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32009R1223",
    assetUrl: `${ICON_BASE}/pao.svg`,
  },
  hourglass: {
    label: "Best before date",
    citation: "Reg. (EC) 1223/2009 Annex VII §1",
    source: "Wikimedia · Hourglass-cosmetics-directive.svg",
    minSizeMm: 5,
    wikimediaUrl: "https://commons.wikimedia.org/wiki/File:Hourglass-cosmetics-directive.svg",
    regulationUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32009R1223",
    assetUrl: `${ICON_BASE}/hourglass.svg`,
  },
  open_book: {
    label: "See enclosed leaflet",
    citation: "ISO 7000 ref. 0790 / Reg. 1223/2009 Annex VII §1",
    source: "Wikimedia · ISO 7000 - Ref-No 0790.svg",
    minSizeMm: 4,
    wikimediaUrl: "https://commons.wikimedia.org/wiki/File:ISO_7000_-_Ref-No_0790.svg",
    regulationUrl: "https://www.iso.org/obp/ui/#iso:grs:7000:0790",
    assetUrl: `${ICON_BASE}/open_book.svg`,
  },
  e_mark: {
    label: "e-mark (estimated nominal quantity)",
    citation: "Directive 76/211/EEC, Annex II §3",
    source: "Wikimedia · Estimated sign.svg",
    minSizeMm: 3,
    wikimediaUrl: "https://commons.wikimedia.org/wiki/File:Estimated_sign.svg",
    regulationUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:31976L0211",
    assetUrl: `${ICON_BASE}/e_mark.svg`,
  },
  mobius: {
    label: "Möbius loop (recyclable)",
    citation: "ISO 7000-1135 / Dir. 94/62/EC",
    source: "Wikimedia · Recycle001.svg",
    minSizeMm: 5,
    wikimediaUrl: "https://commons.wikimedia.org/wiki/File:Recycle001.svg",
    regulationUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:31994L0062",
    assetUrl: `${ICON_BASE}/mobius.svg`,
  },
  triman: {
    label: "Triman (FR recyclability)",
    citation: "Article L. 541-9-3 du Code de l'environnement (Decree 2014-1577)",
    source: "Wikimedia · Pictogramme TRIMAN.svg",
    minSizeMm: 6,
    wikimediaUrl:
      "https://commons.wikimedia.org/wiki/File:Pictogramme_TRIMAN_decret_2014-1577_NOR_DEVP1332154D.svg",
    regulationUrl:
      "https://www.legifrance.gouv.fr/loda/id/JORFTEXT000029827553/",
    assetUrl: `${ICON_BASE}/triman.svg`,
  },
  ghs02_flame: {
    label: "GHS02 — Flammable",
    citation: "Reg. (EC) 1272/2008 (CLP) Annex V §2.6",
    source: "Wikimedia · GHS-pictogram-flamme.svg",
    minSizeMm: 10,
    wikimediaUrl: "https://commons.wikimedia.org/wiki/File:GHS-pictogram-flamme.svg",
    regulationUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:02008R1272-20240115",
    assetUrl: `${ICON_BASE}/ghs02_flame.svg`,
  },
  ghs07_exclamation: {
    label: "GHS07 — Irritant / sensitiser",
    citation: "Reg. (EC) 1272/2008 (CLP) Annex V §3.7",
    source: "Wikimedia · GHS-pictogram-exclam.svg",
    minSizeMm: 10,
    wikimediaUrl: "https://commons.wikimedia.org/wiki/File:GHS-pictogram-exclam.svg",
    regulationUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:02008R1272-20240115",
    assetUrl: `${ICON_BASE}/ghs07_exclamation.svg`,
  },
  ghs08_health: {
    label: "GHS08 — Serious health hazard",
    citation: "Reg. (EC) 1272/2008 (CLP) Annex V §3.8",
    source: "Wikimedia · GHS-pictogram-silhouette.svg",
    minSizeMm: 10,
    wikimediaUrl: "https://commons.wikimedia.org/wiki/File:GHS-pictogram-silhouette.svg",
    regulationUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:02008R1272-20240115",
    assetUrl: `${ICON_BASE}/ghs08_health.svg`,
  },
};

// ---------------------------------------------------------------------------
// React rendering — flat label preview
// ---------------------------------------------------------------------------

const PictogramImg = ({ kind, size = 28, alt }) => {
  const src = ICON_FILE[kind];
  if (!src) return null;
  return (
    <img
      src={src}
      width={size}
      height={size}
      alt={alt || PICTOGRAM_INDEX[kind]?.label || kind}
      loading="lazy"
      style={{ display: "inline-block", verticalAlign: "middle" }}
    />
  );
};

// PAO is a special case: the downloaded file is "12M". When the user wants a
// different month value (e.g., 6M, 24M, 36M), we overlay the months number
// on top of the empty PAO shape via an inline span. For the canonical 12M
// case we use the Wikimedia file directly.
export const PaoPictogram = ({ months, size = 36 }) => {
  const m = String(months || "").trim().toUpperCase();
  if (!m || m === "12M") {
    return <PictogramImg kind="pao" size={size} alt={`Period after opening ${m || "12M"}`} />;
  }
  // Non-12M: overlay the months value on top of the open-jar pictogram.
  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        width: size,
        height: size,
        alignItems: "center",
        justifyContent: "center",
        verticalAlign: "middle",
      }}
      aria-label={`Period after opening ${m}`}
    >
      <img
        src={ICON_FILE.pao}
        width={size}
        height={size}
        alt=""
        loading="lazy"
        style={{ display: "block" }}
      />
      {/* Cover the original "12M" with a white box and write the new value. */}
      <span
        style={{
          position: "absolute",
          left: "50%",
          top: "62%",
          transform: "translate(-50%, -50%)",
          fontFamily: '"Roboto Condensed", sans-serif',
          fontSize: Math.round(size * 0.28),
          fontWeight: 700,
          color: "#1B2A4A",
          background: "#FFFFFF",
          padding: `${Math.round(size * 0.02)}px ${Math.round(size * 0.04)}px`,
          lineHeight: 1,
        }}
      >
        {m}
      </span>
    </span>
  );
};

export const BbePictogram = ({ size = 28 }) => <PictogramImg kind="hourglass" size={size} />;
export const OpenBookPictogram = ({ size = 32 }) => <PictogramImg kind="open_book" size={size} />;
export const EMarkPictogram = ({ size = 24 }) => <PictogramImg kind="e_mark" size={size} />;
export const MobiusPictogram = ({ size = 32 }) => <PictogramImg kind="mobius" size={size} />;
export const TrimanPictogram = ({ size = 32 }) => <PictogramImg kind="triman" size={size} />;

export const Pictogram = ({ kind, size = 28, value }) => {
  switch (kind) {
    case "pao":
      return <PaoPictogram months={value} size={size} />;
    case "hourglass":
      return <BbePictogram size={size} />;
    case "open_book":
      return <OpenBookPictogram size={size} />;
    case "e_mark":
      return <EMarkPictogram size={size} />;
    case "mobius":
      return <MobiusPictogram size={size} />;
    case "triman":
      return <TrimanPictogram size={size} />;
    default:
      // Generic fallthrough for any registered pictogram (e.g. GHS hazard
      // icons) — render the SVG via <img> using the ICON_FILE entry.
      if (ICON_FILE[kind]) {
        return <PictogramImg kind={kind} size={size} />;
      }
      return null;
  }
};

// ---------------------------------------------------------------------------
// Canvas drawing — pre-load images so the 3D texture can drawImage them.
// ---------------------------------------------------------------------------

const imageCache = new Map();

const loadIcon = (kind) =>
  new Promise((resolve, reject) => {
    if (imageCache.has(kind)) {
      resolve(imageCache.get(kind));
      return;
    }
    const src = ICON_FILE[kind];
    if (!src) {
      reject(new Error(`Unknown pictogram kind: ${kind}`));
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imageCache.set(kind, img);
      resolve(img);
    };
    img.onerror = (e) => reject(e);
    img.src = src;
  });

// Pre-load all icons. Returns a promise that resolves when every icon is
// either loaded or has failed; callers can `then(() => buildTextures(...))`.
export const preloadPictograms = () =>
  Promise.allSettled(Object.keys(ICON_FILE).map((kind) => loadIcon(kind)));

// Synchronous draw (used by the texture builder). If the icon hasn't loaded
// yet, draws a placeholder rectangle so the layout doesn't shift.
export const drawPictogram = (ctx, kind, x, y, size, value) => {
  const img = imageCache.get(kind);
  if (img && img.complete && img.naturalWidth > 0) {
    ctx.save();
    ctx.drawImage(img, x, y, size, size);
    // PAO with non-12M months: overlay the new value on top.
    if (kind === "pao" && value && String(value).toUpperCase() !== "12M") {
      const cx = x + size * 0.5;
      const cy = y + size * 0.62;
      const overlayW = size * 0.5;
      const overlayH = size * 0.22;
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(cx - overlayW / 2, cy - overlayH / 2, overlayW, overlayH);
      ctx.fillStyle = "#1B2A4A";
      ctx.font = `700 ${Math.round(size * 0.28)}px "Roboto Condensed", sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(value).toUpperCase(), cx, cy);
    }
    ctx.restore();
    return true;
  }
  // Placeholder while loading
  ctx.save();
  ctx.strokeStyle = "#9CA3AF";
  ctx.lineWidth = 1;
  ctx.strokeRect(x, y, size, size);
  ctx.restore();
  return false;
};
